import React, { useState, useEffect } from "react";
import styles from "./absences-modal.module.css";
import Button from "../../ui/button";
import Input from "../../ui/input";
import { MyIcons } from "../../icons/my-icons";
import {
  getAusencias,
  getWorkers,
  createAusencia,
  updateAusencia,
  deleteAusencia,
} from "../../../services/apiService";
import { showSuccessToast, showErrorToast } from "../../../utils/notifications";

const AbsencesModal = ({ isOpen, onClose }) => {
  const [ausencias, setAusencias] = useState([]);
  const [operarios, setOperarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAusencia, setEditingAusencia] = useState(null);
  
  // Formulario
  const [formData, setFormData] = useState({
    operarioId: "",
    fechaInicio: "",
    fechaFin: "",
    motivo: "",
    observaciones: "",
  });

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ausenciasData, operariosData] = await Promise.all([
        getAusencias(),
        getWorkers(),
      ]);
      setAusencias(ausenciasData || []);
      setOperarios(operariosData || []);
    } catch (error) {
      console.error("Error loading data:", error);
      showErrorToast("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.operarioId) {
      showErrorToast("Selecciona un operario");
      return false;
    }
    if (!formData.fechaInicio) {
      showErrorToast("Indica la fecha de inicio");
      return false;
    }
    if (!formData.fechaFin) {
      showErrorToast("Indica la fecha de fin");
      return false;
    }
    if (new Date(formData.fechaFin) < new Date(formData.fechaInicio)) {
      showErrorToast("La fecha de fin debe ser posterior a la fecha de inicio");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const username = localStorage.getItem("username") || "admin";
      const ausenciaData = {
        operarioId: parseInt(formData.operarioId),
        fechaInicio: formData.fechaInicio,
        fechaFin: formData.fechaFin,
        motivo: formData.motivo || null,
        observaciones: formData.observaciones || null,
        creadoPor: username,
      };

      if (editingAusencia) {
        await updateAusencia(editingAusencia.id, ausenciaData);
        showSuccessToast("Ausencia actualizada correctamente");
      } else {
        await createAusencia(ausenciaData);
        showSuccessToast("Ausencia creada correctamente");
      }

      resetForm();
      loadData();
    } catch (error) {
      console.error("Error saving ausencia:", error);
      showErrorToast("Error al guardar la ausencia");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (ausencia) => {
    setEditingAusencia(ausencia);
    setFormData({
      operarioId: ausencia.operarioId?.toString() || "",
      fechaInicio: ausencia.fechaInicio || "",
      fechaFin: ausencia.fechaFin || "",
      motivo: ausencia.motivo || "",
      observaciones: ausencia.observaciones || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de que quieres eliminar esta ausencia?")) {
      return;
    }

    setLoading(true);
    try {
      await deleteAusencia(id);
      showSuccessToast("Ausencia eliminada correctamente");
      loadData();
    } catch (error) {
      console.error("Error deleting ausencia:", error);
      showErrorToast("Error al eliminar la ausencia");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      operarioId: "",
      fechaInicio: "",
      fechaFin: "",
      motivo: "",
      observaciones: "",
    });
    setEditingAusencia(null);
    setShowForm(false);
  };

  const getOperarioNombre = (operarioId) => {
    const operario = operarios.find((o) => o.id === operarioId);
    return operario ? `${operario.nombre} ${operario.apellidos || ""}`.trim() : "Desconocido";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <MyIcons name="calendar" width={24} height={24} color="var(--main)" />
            Gestión de Ausencias
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            <MyIcons name="close" width={24} height={24} color="var(--text-color)" />
          </button>
        </div>

        <div className={styles.modalBody}>
          {loading && !showForm && (
            <div className={styles.loadingContainer}>
              <p>Cargando...</p>
            </div>
          )}

          {!loading && !showForm && (
            <>
              <div className={styles.headerActions}>
                <Button
                  onClick={() => setShowForm(true)}
                  variant="primary"
                  disabled={loading}
                >
                  <MyIcons name="add" width={20} height={20} color="#fff" />
                  Añadir Ausencia
                </Button>
              </div>

              {ausencias.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>No hay ausencias registradas</p>
                </div>
              ) : (
                <div className={styles.ausenciasList}>
                  {ausencias.map((ausencia) => (
                    <div key={ausencia.id} className={styles.ausenciaCard}>
                      <div className={styles.ausenciaHeader}>
                        <h3 className={styles.operarioName}>
                          {getOperarioNombre(ausencia.operarioId)}
                        </h3>
                        <div className={styles.ausenciaActions}>
                          <button
                            className={styles.iconButton}
                            onClick={() => handleEdit(ausencia)}
                            title="Editar"
                          >
                            <MyIcons name="edit" width={18} height={18} color="var(--main)" />
                          </button>
                          <button
                            className={styles.iconButton}
                            onClick={() => handleDelete(ausencia.id)}
                            title="Eliminar"
                          >
                            <MyIcons name="trash" width={18} height={18} color="var(--danger)" />
                          </button>
                        </div>
                      </div>
                      <div className={styles.ausenciaDetails}>
                        <div className={styles.dateRange}>
                          <MyIcons name="calendar" width={16} height={16} color="#666" />
                          <span>
                            {formatDate(ausencia.fechaInicio)} - {formatDate(ausencia.fechaFin)}
                          </span>
                        </div>
                        {ausencia.motivo && (
                          <div className={styles.motivo}>
                            <strong>Motivo:</strong> {ausencia.motivo}
                          </div>
                        )}
                        {ausencia.observaciones && (
                          <div className={styles.observaciones}>
                            <strong>Observaciones:</strong> {ausencia.observaciones}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {showForm && (
            <div className={styles.formContainer}>
              <div className={styles.formHeader}>
                <h3>{editingAusencia ? "Editar Ausencia" : "Nueva Ausencia"}</h3>
                <button className={styles.backButton} onClick={resetForm}>
                  <MyIcons name="arrowLeft" width={20} height={20} color="var(--main)" />
                  Volver
                </button>
              </div>

              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Operario *</label>
                  <select
                    className={styles.select}
                    value={formData.operarioId}
                    onChange={(e) => handleInputChange("operarioId", e.target.value)}
                    required
                    disabled={loading}
                  >
                    <option value="">Selecciona un operario</option>
                    {operarios
                      .filter((o) => !o.inactivo)
                      .map((operario) => (
                        <option key={operario.id} value={operario.id}>
                          {operario.nombre} {operario.apellidos || ""}
                        </option>
                      ))}
                  </select>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Fecha Inicio *</label>
                    <Input
                      type="date"
                      value={formData.fechaInicio}
                      onChange={(e) => handleInputChange("fechaInicio", e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Fecha Fin *</label>
                    <Input
                      type="date"
                      value={formData.fechaFin}
                      onChange={(e) => handleInputChange("fechaFin", e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Motivo</label>
                  <Input
                    type="text"
                    value={formData.motivo}
                    onChange={(e) => handleInputChange("motivo", e.target.value)}
                    placeholder="Ej: Vacaciones, Baja médica..."
                    disabled={loading}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Observaciones</label>
                  <textarea
                    className={styles.textarea}
                    value={formData.observaciones}
                    onChange={(e) => handleInputChange("observaciones", e.target.value)}
                    placeholder="Notas adicionales..."
                    rows={3}
                    disabled={loading}
                  />
                </div>

                <div className={styles.formActions}>
                  <Button type="button" variant="secondary" onClick={resetForm} disabled={loading}>
                    Cancelar
                  </Button>
                  <Button type="submit" variant="primary" disabled={loading}>
                    {loading ? "Guardando..." : editingAusencia ? "Actualizar" : "Crear"}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AbsencesModal;
